import { useRef, useState } from "react";
import { useAuth } from "@clerk/react";
import { Send, Wrench } from "lucide-react";
import Markdown from "react-markdown";
import { API, useApi, useResource } from "@/lib/api";
import { PageHeader, Panel, JsonView, StatusPill } from "@/components/kit";

const ToolEvent = ({ ev }) => (
  <div className="my-2 rounded-sm border border-[var(--cc-line)] bg-[var(--cc-paper)] p-3" data-testid="console-tool-event">
    <p className="flex items-center gap-2 font-mono text-xs"><Wrench size={12} /> {ev.name}
      {ev.result ? <StatusPill status={ev.result.status} /> : <span className="cc-pulse">calling…</span>}
      {ev.result && <span className="text-[var(--cc-muted)]">audit #{ev.result.audit_seq}</span>}</p>
    {ev.result && <details className="mt-2"><summary className="cursor-pointer text-[11px] text-[var(--cc-info)]">Structured result</summary><JsonView value={ev.result} max="max-h-60" /></details>}
  </div>
);

const useChat = () => {
  const { getToken } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [busy, setBusy] = useState(false);
  const sid = useRef(null);
  const patchLast = (fn) => setMsgs((m) => [...m.slice(0, -1), fn(m[m.length - 1])]);
  const send = async (text) => {
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", text }, { role: "assistant", text: "", tools: [], error: null }]);
    try {
      const res = await fetch(`${API}/console/chat`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${await getToken()}` }, body: JSON.stringify({ message: text, session_id: sid.current }) });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || `HTTP ${res.status}`);
      const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = "";
      for (;;) {
        const { value, done } = await reader.read(); if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const ev = JSON.parse(p.slice(6));
          if (ev.type === "session") sid.current = ev.session_id;
          if (ev.type === "text") patchLast((a) => ({ ...a, text: a.text + ev.content }));
          if (ev.type === "tool_call") patchLast((a) => ({ ...a, tools: [...a.tools, { name: ev.name }] }));
          if (ev.type === "tool_result") patchLast((a) => ({ ...a, tools: a.tools.map((t, i) => (i === a.tools.length - 1 ? { ...t, result: ev.result } : t)) }));
          if (ev.type === "error") patchLast((a) => ({ ...a, error: ev.message }));
        }
      }
    } catch (e) { patchLast((a) => ({ ...a, error: e.message })); }
    setBusy(false);
  };
  return { msgs, busy, send };
};

const ManualTool = ({ tools }) => {
  const call = useApi();
  const [name, setName] = useState("gateway_status");
  const [args, setArgs] = useState("{}");
  const [out, setOut] = useState(null);
  const run = async () => {
    try { setOut(await call("/console/tool", { method: "POST", body: { name, arguments: JSON.parse(args || "{}") } })); }
    catch (e) { setOut({ ok: false, error: e.message }); }
  };
  return (
    <div className="space-y-3">
      <input data-testid="manual-tool-name" list="tool-names" className="w-full rounded-sm border border-[var(--cc-line)] px-3 py-2 font-mono text-xs" value={name} onChange={(e) => setName(e.target.value)} />
      <datalist id="tool-names">{tools.map((t) => <option key={t.name} value={t.name} />)}</datalist>
      <textarea data-testid="manual-tool-args" rows={3} className="w-full rounded-sm border border-[var(--cc-line)] px-3 py-2 font-mono text-xs" value={args} onChange={(e) => setArgs(e.target.value)} />
      <button data-testid="manual-tool-run" onClick={run} className="w-full rounded-sm bg-[var(--cc-navy)] py-2 text-sm text-white hover:bg-[var(--cc-signal)] hover:text-[var(--cc-navy)]">Invoke tool</button>
      {out && <JsonView value={out} testId="manual-tool-result" />}
    </div>
  );
};

const SUGGEST = ["Give me the gateway status", "List the registered agents and their permissions", "List branches in the repository", "Create a commit that updates README.md"];

export default function Console() {
  const { msgs, busy, send } = useChat();
  const [text, setText] = useState("");
  const conn = useResource("/connectors");
  const submit = (e) => { e.preventDefault(); if (text.trim() && !busy) { send(text.trim()); setText(""); } };
  return (
    <div>
      <PageHeader eyebrow="Interim · OpenAI API" title="Developer Console" />
      <div className="mb-6 rounded-sm border-l-4 border-[var(--cc-info)] bg-white p-4 text-sm" data-testid="console-disclaimer">
        Powered by the OpenAI API ({conn.data?.openai_console.model || "gpt-5.5"}) through the Emergent Universal Key. This is <b>not</b> your ChatGPT conversation and has no access to its history. Only L1 read-only tools are available; every tool call is audited.
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Panel title="Conversation" className="lg:col-span-2" testId="console-chat">
          <div className="mb-4 max-h-[520px] min-h-[280px] space-y-4 overflow-auto" data-testid="console-messages">
            {!msgs.length && <div className="flex flex-wrap gap-2">{SUGGEST.map((s) => <button key={s} onClick={() => send(s)} data-testid="console-suggestion" className="rounded-sm border border-[var(--cc-line)] px-3 py-1.5 text-xs hover:border-[var(--cc-navy)]">{s}</button>)}</div>}
            {msgs.map((m, i) => m.role === "user"
              ? <p key={i} className="ml-auto max-w-[85%] rounded-sm bg-[var(--cc-navy)] px-4 py-2 text-sm text-white" data-testid="console-user-message">{m.text}</p>
              : <div key={i} className="max-w-[95%]" data-testid="console-assistant-message">
                  {m.tools.map((t, j) => <ToolEvent key={j} ev={t} />)}
                  {m.text && <div className="prose-sm space-y-2 break-words text-sm leading-relaxed [&_code]:rounded-sm [&_code]:bg-[var(--cc-paper)] [&_code]:px-1 [&_li]:ml-4 [&_ol]:list-decimal [&_table]:block [&_table]:overflow-x-auto [&_td]:border [&_td]:px-2 [&_th]:border [&_th]:px-2 [&_ul]:list-disc"><Markdown>{m.text}</Markdown></div>}
                  {!m.text && !m.error && busy && i === msgs.length - 1 && <p className="cc-pulse text-xs text-[var(--cc-muted)]">Thinking…</p>}
                  {m.error && <p className="text-sm text-[var(--cc-deny)]" data-testid="console-error">{m.error}</p>}
                </div>)}
          </div>
          <form onSubmit={submit} className="flex gap-2">
            <input data-testid="console-input" className="min-w-0 flex-1 rounded-sm border border-[var(--cc-line)] px-3 py-2.5 text-sm outline-none focus:border-[var(--cc-navy)]" placeholder="Ask a read-only question…" value={text} onChange={(e) => setText(e.target.value)} />
            <button data-testid="console-send" disabled={busy} className="rounded-sm bg-[var(--cc-navy)] px-4 text-white hover:bg-[var(--cc-signal)] hover:text-[var(--cc-navy)] disabled:opacity-50"><Send size={16} /></button>
          </form>
        </Panel>
        <Panel title="Direct tool invocation" testId="manual-tool-panel"><ManualTool tools={conn.data?.tools || []} /></Panel>
      </div>
    </div>
  );
}
